export const ImportMethod = {
  CSV: 'csv',
  AccountingSync: 'accounting-sync',
} as const;
export type ImportMethod = (typeof ImportMethod)[keyof typeof ImportMethod];

export const ImportStatus = {
  Idle: 'idle',
  Uploading: 'uploading',
  Processing: 'processing',
  Validating: 'validating',
  Complete: 'complete',
  Error: 'error',
} as const;
export type ImportStatus = (typeof ImportStatus)[keyof typeof ImportStatus];

export const ExclusionReason = {
  AlreadyImported: 'already-imported',
  MissingData: 'missing-data',
  InvalidData: 'invalid-data',
} as const;
export type ExclusionReason =
  (typeof ExclusionReason)[keyof typeof ExclusionReason];

export interface Asset {
  id: string;
  assetCode: string;
  description: string;
  category: string;
  boughtValue: number;
  boughtDate: string;
  supplier?: string;
  serialNumber?: string;
  location?: string;
  notes?: string;
}

export interface CSVValidationError {
  row: number;
  field: string;
  error: string;
  value?: string;
}

export interface CSVValidationWarning {
  row: number;
  field: string;
  message: string;
}

export interface CSVValidationResult {
  valid: boolean;
  totalRows: number;
  validRows: number;
  errorCount: number;
  warningCount: number;
  errors: CSVValidationError[];
  warnings: CSVValidationWarning[];
  validAssets: Asset[];
  previewAssets: Asset[];
  jobId?: string; // Backend import job ID for later import
}

export interface AccountingConnection {
  id: string;
  systemName: string;
  systemType: 'access-financials' | 'dimensions';
  isConnected: boolean;
  lastSyncDate: string | null;
  logoUrl?: string;
  instanceId?: string;
}

export interface AgentCategorySuggestion {
  originalCategory?: string;
  suggestedCategory: string;
  confidence: number;
  reason?: string;
}

export interface AccountingAsset {
  id: string;
  primaryKey?: number;
  assetCode: string;
  description: string;
  category: string;
  purchasePrice: number;
  purchaseDate: string;
  supplier?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  location?: string;
  invoiceReference?: string;
  purchaseOrderNumber?: string;
  notes?: string;
  status: 'new' | 'already-imported' | 'excluded';
  exclusionReason?: ExclusionReason;
  exclusionDetails?: string;
  // Original category from Financials (preserved for AI agent context)
  financialsCategory?: string;
  // AI agent suggestion for category mapping
  agentSuggestion?: AgentCategorySuggestion;
  // Editable overrides
  editedAssetCode?: string;
  editedDescription?: string;
  editedCategory?: string;
}

export interface AssetImportSummary {
  newAssets: number;
  alreadyImported: number;
  excluded: number;
  selected: number;
}

export interface ImportResult {
  success: boolean;
  importedCount: number;
  failedCount: number;
  skippedCount?: number;
  message?: string;
  errors?: string[];
}

export interface FilterOptions {
  dateFrom?: string;
  dateTo?: string;
  category?: string;
}

export interface ParsedCSVRow {
  [key: string]: string;
}

export interface CSVParseResult {
  data: ParsedCSVRow[];
  errors: CSVValidationError[];
}

// Transaction types for asset creation
export const TransactionType = {
  PurchaseInvoice: 'INV',
  PurchaseCreditNote: 'CRN',
  NominalDebit: 'DR',
  NominalCredit: 'CR',
  CostingTimesheet: 'CSTTIM',
  CostingMiscellaneous: 'CSTMSC',
} as const;
export type TransactionType =
  (typeof TransactionType)[keyof typeof TransactionType];

export const TransactionSource = {
  PurchaseLedger: 'purchase-ledger',
  NominalLedger: 'nominal-ledger',
  Costing: 'costing',
} as const;
export type TransactionSource =
  (typeof TransactionSource)[keyof typeof TransactionSource];

export interface Transaction {
  id: string;
  transactionType: TransactionType;
  transactionSource: TransactionSource;
  transactionDate: string;
  transactionReference: string;
  description: string;
  netValue: number;
  grossValue: number;
  vatAmount: number;
  supplier?: string;
  analysisCode?: string;
  nominalAccount?: string;
  category?: string;
  costCentre?: string;
  project?: string;
  quantity?: number;
  serialNumber?: string;
  status: 'eligible' | 'already-used' | 'excluded';
  exclusionReason?: string;
  canCreateAsset: boolean;
  // Editable overrides
  editedAssetCode?: string;
  editedDescription?: string;
  editedCategory?: string;
  // Split option for transactions with quantity > 1
  splitIntoIndividual?: boolean;
}

export interface TransactionFilterOptions {
  dateFrom: Date;
  dateTo: Date;
  transactionSource?: TransactionSource;
  category?: string;
  supplier?: string;
  minValue?: number;
  maxValue?: number;
}

export interface TransactionOverride {
  assetCode?: string;
  description?: string;
  category?: string;
  splitIntoIndividual?: boolean;
}
